import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	SelectChangeEvent,
	styled,
	TextField,
	Typography,
} from "@mui/material";
import { api } from "api";
import {
	Activity,
	ActivityAdd,
	Assignment,
	AssignmentAdd,
	AssignmentForEditing,
	Group,
} from "api/types";
import { InfoSnackBar } from "components/InfoSnackBar";
import { PreviewSelectedActivity } from "components/PreviewSelectedActivity";
import { parseISO } from "date-fns";
import {
	formatDateForAPI,
	formatDateForMUI,
	formatUTCDateStringToLocalISO,
} from "helpers/date";
import { ASSIGNMENT_NEEDS_GROUP, ERR_CREATE } from "messages";
import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { routes } from "routes";

const PREFIX = "AssignmentDetailDialog";

const classes = {
	dialog: `${PREFIX}-dialog`,
	content: `${PREFIX}-content`,
	gridContent: `${PREFIX}-gridContent`,
	action: `${PREFIX}-action`,
	textField: `${PREFIX}-textField`,
	label: `${PREFIX}-label`,
};

const Root = styled("div")(({ theme }) => ({
	[`& .${classes.dialog}`]: {
		padding: theme.spacing(2),
	},

	[`& .${classes.content}`]: {
		padding: theme.spacing(2),
	},

	[`& .${classes.gridContent}`]: {
		marging: theme.spacing(2),
	},

	[`& .${classes.action}`]: {
		margin: 0,
		padding: theme.spacing(1),
	},

	[`& .${classes.textField}`]: {
		marginLeft: theme.spacing(1),
		marginRight: theme.spacing(1),
	},

	[`& .${classes.label}`]: {
		backgroundColor: "white",
		padding: theme.spacing(0, 1),
	},
}));

export interface AssignmentDetailDialogProps {
	mode: "create" | "edit" | "recover";
	detail: Assignment;
	activities: Activity[];
	open: boolean;
	handleClose: () => void;
}

export const AssignmentDetailDialog = ({
	mode,
	detail,
	activities,
	open,
	handleClose,
}: AssignmentDetailDialogProps): JSX.Element => {
	const [groupErr, setGroupErr] = useState({
		empty: true,
		loaded: false,
		error: false,
	});

	const [allGroups, setAllGroups] = useState<Group[]>([]);
	const [assignmentDetail, setAssignmentDetail] =
		useState<AssignmentForEditing>({
			name: "",
			groupId: "",
			startDate: "",
			endDate: "",
		});

	// If they've changed the start date, it can't be a date before today
	const [hasUpdatedStartDate, setHasUpdatedStartDate] = useState(false);

	const [err, setErr] = useState<boolean>(false);
	const history = useHistory();

	useEffect(() => {
		if (mode === "edit" || mode === "recover") {
			setAssignmentDetail({
				...detail,
				startDate: formatUTCDateStringToLocalISO(detail.startDate),
				endDate: formatUTCDateStringToLocalISO(detail.endDate),
			});
		}
		if (!open) {
			return;
		}
		setGroupErr((e) => ({ ...e, loaded: false }));
		api
			.getGroups()
			.then((res) => {
				if (res.status !== 200) {
					setGroupErr(() => ({ empty: false, error: true, loaded: true }));
					return;
				}

				if (res.data?.length === 0 || res.data === null) {
					setAllGroups([]);
					setGroupErr((e) => ({ ...e, loaded: true }));
					return;
				}
				setAllGroups(res.data);
				setGroupErr((e) => ({ ...e, empty: false, loaded: true }));
			})
			.catch(() =>
				setGroupErr(() => ({ empty: false, error: true, loaded: true })),
			);
	}, [open, detail, mode]);

	const handleValueChange =
		(field: keyof AssignmentAdd) =>
		(
			event:
				| React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
				| SelectChangeEvent<string>,
		): void => {
			// We need to know if the start date has been changed. If it has been
			// changed, then we need to check that the start date is not before today.
			if (field === "startDate") {
				setHasUpdatedStartDate(true);
			}

			setAssignmentDetail((currState) => ({
				...currState,
				[field]: event.target.value,
			}));
		};

	const handleAddActivity = (): ActivityAdd[] => {
		const activitiesAdd: ActivityAdd[] = [];
		activities.forEach((item) => {
			activitiesAdd.push({
				questId: item.questId,
				activityType: item.activityType,
			});
		});
		return activitiesAdd;
	};

	const handleCreate = (event: React.FormEvent): void => {
		event.preventDefault();
		const activitiesAdd: ActivityAdd[] = handleAddActivity();
		const assignmentToCreate: AssignmentAdd = {
			...assignmentDetail,
			startDate: formatDateForAPI(assignmentDetail.startDate),
			endDate: formatDateForAPI(assignmentDetail.endDate),
			activities: activitiesAdd,
		};

		api
			.addAssignment(assignmentToCreate)
			.then((res) => {
				if (res.status !== 201) {
					setErr(true);
					return;
				}

				history.push(routes._.assignments.path);
			})
			.catch(() => {
				setErr(true);
			});
	};

	const handleEdit = (event: React.FormEvent): void => {
		event.preventDefault();
		api
			.updateAssignment(detail.assignmentId, {
				...assignmentDetail,
				startDate: formatDateForAPI(assignmentDetail.startDate),
				endDate: formatDateForAPI(assignmentDetail.endDate),
			})
			.then((res) => {
				if (res.status !== 204) {
					setErr(true);
					return;
				}

				history.push(routes._.assignments.path);

			})
			.catch(() => {
				setErr(true);
			});
	};

	// const renderValue = ():

	const createGroup = (): void => {
		// open a new tab to create a group
		window.open(routes._.groups._.create.path, "_blank", "noopener,noreferrer");
		handleClose();
	};

	const isSubmitButtonDisabled = useMemo(
		() =>
			err ||
			!groupErr.loaded ||
			groupErr.error ||
			groupErr.empty ||
			assignmentDetail.name.trim() === "" ||
			assignmentDetail.startDate.trim() === "" ||
			assignmentDetail.endDate.trim() === "" ||
			(hasUpdatedStartDate &&
				parseISO(assignmentDetail.startDate) < new Date()) ||
			parseISO(assignmentDetail.startDate) > parseISO(assignmentDetail.endDate),
		[
			err,
			groupErr.loaded,
			groupErr.error,
			groupErr.empty,
			assignmentDetail.name,
			assignmentDetail.startDate,
			assignmentDetail.endDate,
			hasUpdatedStartDate,
		],
	);

	function getDialogTitle(): string {
		switch (mode) {
			case "create": {
				return "Create Assignment";
				break;
			}
			case "edit": {
				return "Edit Assignment";
				break;
			}
			default: {
				return "Recover Assignment";
				break;
			}
		}
	}
	return (
		<Root>
			<Dialog
				onClose={handleClose}
				aria-labelledby="customized-dialog-title"
				open={open}
				className={classes.dialog}
				maxWidth="xl"
				fullWidth
			>
				<DialogTitle>{getDialogTitle()}</DialogTitle>
				{groupErr.loaded && groupErr.error && (
					<Typography variant="h3">{ERR_CREATE}</Typography>
				)}
				{groupErr.loaded && groupErr.empty && (
					<DialogContent>
						<Typography variant="subtitle1">
							{ASSIGNMENT_NEEDS_GROUP}
						</Typography>
						<Button color="primary" variant="contained" onClick={createGroup}>
							Create group
						</Button>
					</DialogContent>
				)}
				{groupErr.loaded && !groupErr.empty && !groupErr.error && (
					<form onSubmit={mode === "create" ? handleCreate : handleEdit}>
						<DialogContent dividers>
							<Box display="flex">
								<Box width="50%" m={1}>
									<TextField
										id="name"
										label="Name"
										variant="outlined"
										value={assignmentDetail.name}
										fullWidth
										required
										InputLabelProps={{
											shrink: true,
										}}
										inputProps={{
											"data-testid": "nameInput",
										}}
										placeholder="Assignment Name"
										onChange={handleValueChange("name")}
									/>
								</Box>
								<Box width="50%" m={1}>
									<FormControl variant="outlined" fullWidth>
										<InputLabel id="groupLabel" className={classes.label}>
											Group
										</InputLabel>
										<Select
											id="group"
											labelId="groupLabel"
											fullWidth
											value={
												allGroups.find(
													(group) => group.groupId === assignmentDetail.groupId,
												)?.groupId
											}
											onChange={handleValueChange("groupId")}
											MenuProps={{
												autoFocus: true,
											}}
											data-testid="groupInput"
											placeholder="Group"
										>
											{allGroups.map((group) => (
												<MenuItem key={group.groupId} value={group.groupId}>
													{group.name}
												</MenuItem>
											))}
										</Select>
									</FormControl>
								</Box>
							</Box>
							<Box display="flex">
								<Box display="flex" width="50%" m={1} minWidth="250px">
									<TextField
										id="startDate"
										label="Start Date"
										type="datetime-local"
										variant="outlined"
										error={
											hasUpdatedStartDate &&
											parseISO(assignmentDetail.startDate) < new Date()
										}
										helperText={
											mode === "create" &&
											"New assignments must cannot be start before today"
										}
										value={
											assignmentDetail.startDate &&
											formatDateForMUI(assignmentDetail.startDate)
										}
										inputProps={{
											"data-testid": "start-date",
										}}
										className={classes.textField}
										fullWidth
										required
										InputLabelProps={{
											shrink: true,
										}}
										onChange={handleValueChange("startDate")}
									/>
								</Box>
								<Box display="flex" width="50%" m={1} minWidth="250px">
									<TextField
										id="dueDate"
										label="Due Date"
										type="datetime-local"
										variant="outlined"
										error={
											parseISO(assignmentDetail.endDate) <
											parseISO(assignmentDetail.startDate)
										}
										helperText="Due date must be after start date"
										value={
											assignmentDetail.endDate &&
											formatDateForMUI(assignmentDetail.endDate)
										}
										inputProps={{
											"data-testid": "due-date",
										}}
										className={classes.textField}
										fullWidth
										required
										InputLabelProps={{
											shrink: true,
										}}
										onChange={handleValueChange("endDate")}
									/>
								</Box>
							</Box>
							{mode === "create" && (
								<PreviewSelectedActivity
									selected={activities}
									show={false}
									mode={mode}
								/>
							)}
						</DialogContent>
						<DialogActions className={classes.action}>
							<Button
								onClick={handleClose}
								color="primary"
								data-testid="crt-cancel-btn"
							>
								Cancel
							</Button>
							<Button
								autoFocus
								color="primary"
								type="submit"
								data-testid="crt-btn"
								disabled={isSubmitButtonDisabled}
							>
								{mode === "create" ? "CREATE ASSIGNMENT" : "UPDATE ASSIGNMENT"}
							</Button>
						</DialogActions>
					</form>
				)}
			</Dialog>
			<InfoSnackBar
				isOpen={err}
				severity="error"
				onClose={() => setErr(false)}
				message={ERR_CREATE}
			/>
		</Root>
	);
};
